import { useMemo } from "react";

import { Stack, Typography, useTheme } from "@mui/material";

import { formatName } from "igfd-ui/src/utils/stringUtils";

import { useRestaurantContext } from "../../hooks/useRestaurantContext";
import { ProductListItem } from "./product-list-item";

interface ProductListProps {
  dishId: string;
  filter: string;
  selectedAllergenIds: string[];
  optionalProductIds: string[];
  onProductClick: (productId: string) => void;
}

export const ProductList = ({
  dishId,
  filter,
  selectedAllergenIds,
  optionalProductIds,
  onProductClick,
}: ProductListProps) => {
  const { palette } = useTheme();
  const { linkedItems } = useRestaurantContext();

  const dishLinkedItems = linkedItems?.dishes[dishId];

  // Determines sorting priority: Optional > With allergens > Without allergens
  const getSortValue = (isOptional: boolean, hasAllergens: boolean) => {
    if (isOptional) return 0;
    if (hasAllergens) return 1;
    return 2;
  };

  const filteredProducts = useMemo(() => {
    // Filter products based on search filter and selected allergens
    return (
      dishLinkedItems?.directlyLinkedProducts
        .filter((prod) => {
          const product = linkedItems?.products[prod.id];
          const matchesFilter =
            !filter ||
            product?.name.toLowerCase().includes(filter.toLowerCase());
          const matchesAllergens = selectedAllergenIds?.length
            ? product?.allAllergens?.some((allergen) =>
                selectedAllergenIds.includes(allergen.igfdTaxAllergenId)
              )
            : true;
          return matchesFilter && matchesAllergens;
        })
        // Sort products by optional status and allergen presence
        .sort((a, b) => {
          const productA = linkedItems?.products[a.id];
          const productB = linkedItems?.products[b.id];

          const isOptionalA = optionalProductIds?.includes(a.id) ?? false;
          const isOptionalB = optionalProductIds?.includes(b.id) ?? false;

          const hasAllergensA = (productA?.allAllergens?.length ?? 0) > 0;
          const hasAllergensB = (productB?.allAllergens?.length ?? 0) > 0;

          return (
            getSortValue(isOptionalA, hasAllergensA) -
            getSortValue(isOptionalB, hasAllergensB)
          );
        })
    );
  }, [
    filter,
    selectedAllergenIds,
    dishLinkedItems,
    linkedItems,
    optionalProductIds,
  ]);

  const showProducts =
    filteredProducts ?? dishLinkedItems?.directlyLinkedProducts;

  // Nothing to say at zero: "N items in this dish" above already said it.
  if (!showProducts?.length) return null;

  return (
    <Stack width="100%">
      <Typography variant="labelRMedium" mb={1} color="text.secondary">
        {`${showProducts.length} ${
          showProducts.length === 1 ? "Product" : "Products"
        }`}
      </Typography>

      {showProducts.map(({ id }, index) => {
        const product = linkedItems?.products[id];
        const isOptional = optionalProductIds?.includes(id);
        const hasBorder = index < showProducts.length - 1;

        return (
          <ProductListItem
            key={product?.name}
            title={formatName(product?.name || "")}
            subtitle={isOptional ? "Optional product" : undefined}
            preference={product?.mostRestrictivePreference?.preferenceName}
            allergenCount={product?.allAllergens?.length || 0}
            hasBorder={hasBorder}
            onClick={() => onProductClick(id)}
          />
        );
      })}
    </Stack>
  );
};
